import React, { useState, useCallback} from 'react';
import { Header } from '../../components/layout';
import { Card, Pagination } from '../../components/common';
import {
  IdentityTable,
  IdentityFilters,
  IdentityDetail
} from '../../components/modules/identities';
import { StatsCard } from '../../components/modules/overview';
import { useIdentityContext } from '../../context/IdentityContext';
import { useIdentities } from '../../hooks/useIdentities';
import { useStats } from '../../hooks/useStats';
import { identitiesToCSV, downloadFile, formatType } from '../../utils/helpers';
import { IDENTITY_TYPES } from '../../utils/constants';
import {
  FaRobot,
  FaCube,
  FaCog,
  FaIdBadge
} from 'react-icons/fa';
import './Identities.css';

/**
 * NonHumanIdentities Page
 * Page for viewing non-human identities (applications, service principals, managed identities)
 */
const NonHumanIdentities = ({ type = null }) => {
  const { stats, loading: statsLoading } = useStats();
  
  // Build initial type filter based on props
  const getInitialType = () => {
    if (type) return type;
    return [
      IDENTITY_TYPES?.APPLICATION ?? "",
      IDENTITY_TYPES?.SERVICE_PRINCIPAL ?? "",
      IDENTITY_TYPES?.MANAGED_IDENTITY ?? ""
    ].join(',');
  };

  const {
    identities,
    pagination,
    loading,
    filters,
    updateFilters,
    setPage,
    setSearch,
    resetFilters
  } = useIdentities({ type: getInitialType() });

  const {
    selectedIdentity,
    isDetailModalOpen,
    openIdentityDetail,
    closeIdentityDetail
  } = useIdentityContext();

  const [searchValue, setSearchValue] = useState('');

  const handleSearch = useCallback((value) => {
    setSearchValue(value);
    setSearch(value);
  }, [setSearch]);

  const handleExport = useCallback(() => {
    if (identities && identities.length > 0) {
      const csv = identitiesToCSV(identities);
      const typeName = type ? type : 'non-human';
      const filename = `${typeName}-identities-export-${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(csv, filename);
    }
  }, [identities, type]);

  const getTitle = () => {
    if (type) return `${formatType(type)} Identities`;
    return 'Non-Human Identities';
  };

  const getSubtitle = () => {
    if (type === (IDENTITY_TYPES?.APPLICATION ?? "")) {
      return 'View and manage application registrations';
    }
    if (type === (IDENTITY_TYPES?.SERVICE_PRINCIPAL ?? "")) {
      return 'View and manage service principals';
    }
    if (type === (IDENTITY_TYPES?.MANAGED_IDENTITY ?? "")) {
      return 'View and manage managed identities';
    }
    return 'View and manage applications, service principals, and managed identities';
  };

  const nonHumanStats = [
    {
      title: 'Total Non-Human',
      value: stats?.nonHuman,
      icon: <FaRobot />,
      iconColor: 'success',
      show: !type
    },
    {
      title: 'Applications',
      value: stats?.applications,
      icon: <FaCube />,
      iconColor: 'success',
      show: !type || type === IDENTITY_TYPES?.APPLICATION
    },
    {
      title: 'Service Principals',
      value: stats?.servicePrincipals,
      icon: <FaCog />,
      iconColor: 'warning',
      show: !type || type === IDENTITY_TYPES?.SERVICE_PRINCIPAL
    },
    {
      title: 'Managed Identities',
      value: stats?.managedIdentities,
      icon: <FaIdBadge />,
      iconColor: 'info',
      show: !type || type === IDENTITY_TYPES?.MANAGED_IDENTITY
    }
  ].filter(stat => stat.show);

  return (
    <div className="identities-page">
      <Header
        title={getTitle()}
        subtitle={getSubtitle()}
        showSearch={true}
        showSync={true}
        showExport={true}
        searchValue={searchValue}
        onSearch={handleSearch}
        onExport={handleExport}
      />

      {/* Non-Human Stats */}
      <section className="non-human-stats" style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${Math.min(nonHumanStats.length, 4)}, 1fr)`,
        gap: '20px',
        marginBottom: '24px'
      }}>
        {nonHumanStats.map((stat, index) => (
          <StatsCard
            key={index}
            title={stat?.title}
            value={stat?.value}
            icon={stat?.icon}
            iconColor={stat?.iconColor}
            loading={statsLoading}
          />
        ))}
      </section>

      {/* Identity List */}
      <Card padding={false}>
        <IdentityFilters
          filters={filters}
          onFilterChange={updateFilters}
          onReset={resetFilters}
          showTypeFilter={!type}
          allowedTypes={[
            IDENTITY_TYPES?.APPLICATION,
            IDENTITY_TYPES?.SERVICE_PRINCIPAL,
            IDENTITY_TYPES?.MANAGED_IDENTITY
          ]}
        />
        
        <IdentityTable
          identities={identities}
          loading={loading}
          onRowClick={openIdentityDetail}
          onViewDetails={openIdentityDetail}
        />

        {pagination && pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination?.page ?? 0}
            totalPages={pagination?.totalPages ?? 0}
            totalItems={pagination?.total ?? 0}
            itemsPerPage={pagination?.limit ?? 10}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Identity Detail Modal */}
      <IdentityDetail
        identity={selectedIdentity}
        isOpen={isDetailModalOpen}
        onClose={closeIdentityDetail}
      />
    </div>
  );
};

export default NonHumanIdentities;